import { ClipboardCheck } from "lucide-react";
import { useEffect, useState } from "react";

import { PendingDoctorsTable } from "@/components/admin/pending-doctors-table";
import { RefreshButton } from "@/components/admin/refresh-button";
import { useDoctorReview } from "@/components/admin/use-doctor-review";
import { SearchBar } from "@/components/common/search-bar";
import { useAdminUi } from "@/contexts/AdminUiContext";
import { usePendingDoctors } from "@/hooks/admin/use-admin-doctors";
import { usePageMeta } from "@/lib/page-meta";

export function AdminDoctorsRoute() {
  usePageMeta({
    title: "Pending Doctors — HealNova Admin Portal",
    description:
      "Search, review, approve or reject every HealNova doctor registration waiting for verification.",
    robots: "noindex",
  });
  return <PendingDoctorsPage />;
}

function PendingDoctorsPage() {
  const { doctors, isLoading, refresh, refreshing } = usePendingDoctors();
  const { registerRefresh, searchRef } = useAdminUi();
  const { openDoctor, askApprove, askReject, dialogs } = useDoctorReview();
  const [search, setSearch] = useState("");

  useEffect(() => {
    registerRefresh(refresh);
    return () => registerRefresh(null);
  }, [registerRefresh, refresh]);

  useEffect(() => {
    const node = searchRef.current;
    if (!node) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSearch("");
    };
    node.addEventListener("keydown", onKeyDown);
    return () => node.removeEventListener("keydown", onKeyDown);
  }, [searchRef]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            <ClipboardCheck className="size-3.5" aria-hidden="true" /> Doctor approvals
          </span>
          <h1 className="mt-1 font-display text-3xl font-semibold text-balance">
            Pending doctors
            {!isLoading && (
              <span className="ml-3 align-middle text-base font-medium text-muted-foreground tabular-nums">
                {doctors.length}
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Select a doctor to inspect their full registration before approving or rejecting.
          </p>
        </div>
        <RefreshButton onRefresh={() => void refresh()} refreshing={refreshing} />
      </header>

      <SearchBar
        ref={searchRef}
        value={search}
        onChange={setSearch}
        placeholder="Search by name, specialization, hospital or email   /"
        aria-label="Search pending doctors"
        className="max-w-xl"
      />

      <PendingDoctorsTable
        doctors={doctors}
        loading={isLoading}
        search={search}
        onOpenDoctor={openDoctor}
        onApprove={askApprove}
        onReject={askReject}
      />

      {dialogs}
    </div>
  );
}
